import React, { useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import styles from './scene-objects.module.css';
import { TreeNode } from './TreeNode';
import { AssetNode } from './AssetNode';
import { NodeState } from './types';

interface SceneObjectsProps {
  assets?: any[];
  selectObject?: (object: THREE.Object3D) => void;
  deleteAsset?: (asset: any) => void;
  camera?: THREE.Camera;
  scene?: THREE.Scene;
}

export default function SceneObjects({
  assets,
  selectObject,
  deleteAsset,
  camera,
  scene,
}: SceneObjectsProps) {
  const [selectedObject, setSelectedObject] = useState<THREE.Object3D | null>(
    null
  );
  const [nodeStates, setNodeStates] = useState<NodeState>({});

  const handleSelectObject = useCallback(
    (object: THREE.Object3D) => {
      setSelectedObject(object);
      if (selectObject) {
        selectObject(object);
      }
    },
    [selectObject]
  );

  const handleToggleExpand = useCallback((objectId: number) => {
    setNodeStates((prev) => ({
      ...prev,
      [objectId]: !prev[objectId],
    }));
  }, []);

  const sceneHierarchy = useMemo(() => {
    if (!scene) return null;

    const nodes: React.ReactElement[] = [];

    if (camera && !scene.children.includes(camera)) {
      nodes.push(
        <TreeNode
          key={`camera-${camera.id}`}
          object={camera}
          level={0}
          isSelected={selectedObject?.id === camera.id}
          nodeStates={nodeStates}
          onSelect={handleSelectObject}
          onToggleExpand={handleToggleExpand}
        />
      );
    }

    nodes.push(
      <TreeNode
        key={`scene-${scene.id}`}
        object={scene}
        level={0}
        isSelected={selectedObject?.id === scene.id}
        nodeStates={nodeStates}
        onSelect={handleSelectObject}
        onToggleExpand={handleToggleExpand}
      />
    );

    return nodes;
  }, [
    scene,
    camera,
    selectedObject,
    nodeStates,
    handleSelectObject,
    handleToggleExpand,
  ]);

  const assetHierarchy = useMemo(() => {
    if (!assets || assets.length === 0) return null;

    return assets.map((asset, index) => (
      <AssetNode key={`asset-${index}`} asset={asset} index={index} />
    ));
  }, [assets]);

  return (
    <div className={styles.root}>
      <div
        className={styles.outliner}
        style={{
          color: '#444',
          backgroundColor: '#fff',
          padding: '0',
          width: '100%',
          height: '180px',
          fontSize: '12px',
          cursor: 'default',
          overflow: 'auto',
          outline: 'none',
        }}
        tabIndex={0}
      >
        {scene ? sceneHierarchy : assetHierarchy}
      </div>
    </div>
  );
}
